import type { HttpRequestOptions } from '../http/types';
import type { ApiEnvelope } from '../types';
import type { ApiModuleDeps } from './internal/request';
import { envelope } from './internal/request';
import type { ApiHomeLoginObservation } from '../types/login-observation';
import type {
    AccountCenterDisplayMessageResponse,
    ApiClaimResult,
    ApiClassroomQuestsData,
    ApiInvite,
    ApiProject,
    ApiUserInfo,
    ApiUserUtmResponse,
    ChangeEmailResponse,
    ConfirmMergeResponse,
    CreatorTemplate,
    LocalizedContentQueryOptions,
    PublicUserProfile,
    ReferralRewardsResponse,
} from '../types';

export function createUsersApi({ authHttp, noAuthHttp }: ApiModuleDeps) {
    return {
        /** 固定启动事实走现有用户认证；调用方负责授权、原周期及有界取消。 */
        reportLoginObservation(observation: ApiHomeLoginObservation, signal: AbortSignal): Promise<ApiEnvelope<null>> {
            return envelope(authHttp.post('/users/report_login_observation', observation, {
                signal, timeoutMs: 3000, retry: 'never', errorHandling: 'caller',
            }));
        },

        async getUserInfo(
            signal?: AbortSignal,
            options?: Pick<HttpRequestOptions, 'headers'>
        ): Promise<ApiEnvelope<ApiUserInfo>> {
            const response = await authHttp.get<ApiEnvelope<ApiUserInfo>>('/users/info', {
                ...options,
                signal,
            });
            return response;
        },

        async confirmAccountCenterMerge(
            mergeCandidateId: string
        ): Promise<ApiEnvelope<ConfirmMergeResponse>> {
            const response = await authHttp.post<ApiEnvelope<ConfirmMergeResponse>>(
                '/account-center/confirm-merge',
                {
                    merge_candidate_id: mergeCandidateId,
                }
            );
            return response;
        },

        async sendAccountCenterEmailVerification(
            newEmail?: string
        ): Promise<ApiEnvelope<AccountCenterDisplayMessageResponse>> {
            const response = await authHttp.post<ApiEnvelope<AccountCenterDisplayMessageResponse>>(
                '/account-center/email/verification',
                newEmail ? { new_email: newEmail } : {}
            );
            return response;
        },

        async verifyAccountCenterEmail(
            code: string
        ): Promise<ApiEnvelope<AccountCenterDisplayMessageResponse>> {
            const response = await authHttp.post<ApiEnvelope<AccountCenterDisplayMessageResponse>>(
                '/account-center/email/verify',
                {
                    code,
                }
            );
            return response;
        },

        async changeAccountCenterEmail(
            newEmail: string,
            code: string
        ): Promise<ApiEnvelope<ChangeEmailResponse>> {
            const response = await authHttp.post<ApiEnvelope<ChangeEmailResponse>>(
                '/account-center/email',
                {
                    new_email: newEmail,
                    code,
                }
            );
            return response;
        },

        async changeAccountCenterPassword(
            newPassword: string
        ): Promise<ApiEnvelope<AccountCenterDisplayMessageResponse>> {
            const response = await authHttp.post<ApiEnvelope<AccountCenterDisplayMessageResponse>>(
                '/account-center/password',
                {
                    new_password: newPassword,
                }
            );
            return response;
        },

        async getReferralRewards(): Promise<ApiEnvelope<ReferralRewardsResponse>> {
            const response =
                await authHttp.get<ApiEnvelope<ReferralRewardsResponse>>('/referral/rewards');
            return response;
        },

        async getClassroomQuests(
            options: LocalizedContentQueryOptions
        ): Promise<ApiEnvelope<{ quests: ApiClassroomQuestsData }>> {
            const response = await authHttp.get<ApiEnvelope<{ quests: ApiClassroomQuestsData }>>(
                '/classroom/quests',
                {
                    query: { locale: options.locale },
                    signal: options.signal,
                }
            );
            return response;
        },

        async claimClassroomReward(
            questId: string
        ): Promise<ApiEnvelope<{ result: ApiClaimResult }>> {
            const response = await authHttp.post<ApiEnvelope<{ result: ApiClaimResult }>>(
                `/classroom/quests/${questId}/claim`
            );
            return response;
        },

        async reportClassroomQuestCompletion(questId: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/classroom/quests/${questId}/report`
            );
            return response;
        },

        async startClassroomQuest(questId: string): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>(
                `/classroom/quests/${questId}/start`
            );
            return response;
        },

        async reportUserUtm(data: {
            /** 来源平台（如 wechat, google） */
            utm_source?: string;
            /** 媒介类型（如 cpc, social, email） */
            utm_medium?: string;
            /** 活动名称（如 2026_spring_sale） */
            utm_campaign?: string;
            /** 搜索关键词 */
            utm_term?: string;
            /** 素材标识（如 banner_main） */
            utm_content?: string;
            /** UTM 链接 ID，用于关联到具体链接 */
            link_id?: number | null;
            /** 原始 URL 查询字符串（不包含 ?） */
            raw_query?: string;
        }): Promise<ApiEnvelope<null>> {
            const response = await authHttp.post<ApiEnvelope<null>>('/users/utm', data);
            return response;
        },

        async getUserUtm(): Promise<ApiEnvelope<ApiUserUtmResponse>> {
            const response = await authHttp.get<ApiEnvelope<ApiUserUtmResponse>>('/users/utm');
            return response;
        },

        async joinWaitlist(): Promise<ApiEnvelope<{ project: ApiProject }>> {
            const response =
                await authHttp.post<ApiEnvelope<{ project: ApiProject }>>('/users/join-waitlist');
            return response;
        },

        async getUserOpenProjects(
            workspaceId: number,
            withCoverUrl?: boolean
        ): Promise<ApiEnvelope<{ projects: ApiProject[] }>> {
            const response = await authHttp.get<ApiEnvelope<{ projects: ApiProject[] }>>(
                `/workspaces/${workspaceId}/presence/user-projects`
            );
            const result = response;

            // 处理 cover 字段转换为 cover_url
            if (withCoverUrl && result.data?.projects) {
                const projects = result.data.projects;
                const projectsWithCover = projects.filter((project: ApiProject) => project.cover);
                if (projectsWithCover.length > 0) {
                    const { createPublicResourceResolver } = await import(
                        './resources/public-url-resolver'
                    );
                    const { getDownloadPublicResourcePresignedURLs } =
                        createPublicResourceResolver(noAuthHttp);
                    const coverResources = projectsWithCover.map(
                        (project: ApiProject) => project.cover!
                    );
                    const urlsResp = await getDownloadPublicResourcePresignedURLs(coverResources);
                    if (urlsResp.code === 0) {
                        const urlsMap = urlsResp.data.urls;
                        projects.forEach((project: ApiProject) => {
                            if (project.cover && urlsMap[project.cover]) {
                                project.cover_url = urlsMap[project.cover];
                            }
                        });
                    }
                }
            }

            return result;
        },

        async updateUserProfile(profile: {
            name?: string;
            profile_image_key?: string;
            description?: string;
            link?: string;
            x?: string;
        }): Promise<ApiEnvelope<{ user: ApiUserInfo['user'] }>> {
            const response = await authHttp.post<ApiEnvelope<{ user: ApiUserInfo['user'] }>>(
                '/users/profile',
                profile
            );
            return response;
        },

        async getUserPublicProfile(
            user_id: number | string
        ): Promise<ApiEnvelope<{ user: PublicUserProfile }>> {
            const response = await noAuthHttp.get<ApiEnvelope<{ user: PublicUserProfile }>>(
                `/users/${user_id}/profile`
            );
            return response;
        },

        getUserTemplates(
            userId: number | string
        ): Promise<ApiEnvelope<{ templates: CreatorTemplate[] }>> {
            return noAuthHttp.get<ApiEnvelope<{ templates: CreatorTemplate[] }>>(
                '/users/' + encodeURIComponent(String(userId)) + '/templates'
            );
        },

        getInviteInfo(code: string): Promise<ApiEnvelope<{ invite: ApiInvite }>> {
            return noAuthHttp.get('/invites/' + encodeURIComponent(code));
        },

        acceptInvite(code: string): Promise<ApiEnvelope<{ invite: ApiInvite }>> {
            return authHttp.post('/invites/' + encodeURIComponent(code) + '/accept');
        },
    };
}
